// Shared by Invoice.js and Invoice2.js. Recomputes the "Consulting Fixed Fee Offset" line(s)
// for the current lineItems, based on the consultant's fixed-fee contracts and the invoice's
// work start date. Called fresh on every line item change (ProductType, quantity, etc.) so it
// always reflects the current state -- no separate diffing needed.
//
// Each offset line is inserted directly after the source line item it's offsetting, so it
// stays put (e.g. position #2) instead of drifting to the end as more lines get added.
//
// Existing offset lines that already have a recordID (saved to FM) are updated in place rather
// than removed, so we never have to route them through the itemDelete tracking used for
// manual row deletes. If they're no longer needed, they're zeroed out instead of deleted.
const OFFSET_LABEL = "Consulting Fixed Fee Offset";
const QUALIFYING_TYPES = ["Consultant - Hourly", "Consultant - Daily"];

export function applyFixedFeeOffset(lineItems, fixedFeeContracts, workDateISO) {
  const nonOffsetLines = lineItems.filter(li => li.ProductDetail !== OFFSET_LABEL);
  const existingOffsetLines = lineItems.filter(li => li.ProductDetail === OFFSET_LABEL);

  const workDate = workDateISO ? new Date(workDateISO) : null;
  const activeContract = workDate && Array.isArray(fixedFeeContracts)
    ? fixedFeeContracts.find(c =>
        c.StartDate && c.EndDate && workDate >= new Date(c.StartDate) && workDate <= new Date(c.EndDate)
      )
    : null;

  // Walk the non-offset lines in order, inserting an offset line right after each
  // qualifying line it applies to -- so it stays next to its source instead of moving.
  // Offsetting is no longer capped at the contract's guaranteed hours: once a consultant
  // exceeds their hour bank, LRI settles the overage with a separate lump-sum payment at
  // year end instead of paying it out per-invoice, so qualifying hours keep getting netted
  // to $0 on the invoice for as long as the contract's date range is active.
  const result = [];
  let existingIndex = 0;

  for (const li of nonOffsetLines) {
    result.push(li);

    if (!activeContract || !QUALIFYING_TYPES.includes(li.ProductType)) continue;

    const qty = parseFloat(li.quantity) || 0;
    const rate = parseFloat(li.rate) || 0;
    const applied = qty;

    if (applied > 0) {
      result.push({
        ...(existingOffsetLines[existingIndex] || {}),
        ProductType: li.ProductType,
        ProductDetail: OFFSET_LABEL,
        quantity: applied,
        rate: (-rate).toFixed(2),
        FixedFeeContract: activeContract.ContractID
      });
      existingIndex++;
    }
  }

  // Any leftover existing offset lines are no longer needed. Zero out saved ones (rather
  // than delete, so we don't need itemDelete tracking); drop unsaved ones outright.
  for (let i = existingIndex; i < existingOffsetLines.length; i++) {
    const existing = existingOffsetLines[i];
    if (existing.recordID) {
      result.push({ ...existing, quantity: 0, rate: "0.00" });
    }
  }

  return result;
}
